/**
 * Performance timing abstraction for analyzing when experiment tracking fires
 *
 * ## Metrics Captured:
 *
 * 1. **experiment_fired_at** (PRIMARY) - Milliseconds from page start to when tracking fired
 *    → Compare medians across variants to find systematic delays
 *
 * 2. **page_interactive_at** - Milliseconds until page became interactive (React can hydrate)
 *    → If variants differ here, one has slower HTML/initial resource loading
 *
 * 3. **experiment_delay_after_interactive** - Milliseconds between interactive and tracking
 *    → Positive = tracking fired after interactive (normal for useEffect in React)
 *    → Negative = tracking fired BEFORE interactive (e.g. inline script tag)
 *    → If variants differ here, one has slower React component lifecycle
 *
 * 4. **page_fully_loaded_at** - Milliseconds until page fully loaded (loadEventEnd)
 *    → Will be 0 if load event hasn't completed yet (common in Next.js/React apps)
 *    → Use this to understand if tracking fires before/after full page load
 *
 * 5. **network_plus_response_time** - Milliseconds for complete network time
 *    → Includes DNS lookup, TCP connection, SSL handshake, request, and response
 *    → If variants differ here, check if server processing or connection setup differs
 *
 * 6. **time_to_first_byte** - Milliseconds from request sent to first byte received (TTFB)
 *    → Measures server-side processing time (SSR, API calls, database queries)
 *    → Key metric for identifying backend performance differences between variants
 *
 * 7. **first_contentful_paint_at** - Milliseconds until first visual content (optional)
 *    → May be 0 if browser doesn't support paint timing
 *
 * ## Analysis Example:
 * ```
 * Variant A:
 *   experiment_fired_at = 2500ms
 *   page_interactive_at = 1500ms
 *   experiment_delay_after_interactive = 1000ms (positive - fired AFTER interactive)
 *   page_fully_loaded_at = 3200ms (load event completed before tracking)
 *   network_plus_response_time = 450ms (total network time including DNS/TCP/SSL)
 *   time_to_first_byte = 180ms (server processing time)
 *
 * Variant B:
 *   experiment_fired_at = 3000ms
 *   page_interactive_at = 1500ms
 *   experiment_delay_after_interactive = 1500ms (positive - fired AFTER interactive)
 *   page_fully_loaded_at = 0 (load event not yet complete - common in Next.js!)
 *   network_plus_response_time = 650ms (200ms slower - could be server OR connection)
 *   time_to_first_byte = 350ms (170ms slower - SERVER PROCESSING is the issue!)
 *
 * → Both variants become interactive at same time
 * → Variant B fires 500ms later but BEFORE load event completes
 * → B's experiment_delay_after_interactive is 500ms longer = slower React component
 * → B's page_fully_loaded_at = 0 means lazy-loaded assets still loading
 * → B's time_to_first_byte is 170ms slower = variant B does more server-side work
 * → This explains most of the network_plus_response_time difference!
 *
 * Edge case - Tracking fires very early:
 * Variant C:
 *   experiment_fired_at = 800ms
 *   page_interactive_at = 1500ms
 *   experiment_delay_after_interactive = -700ms (negative - fired BEFORE interactive!)
 *   page_fully_loaded_at = 0 (definitely not loaded yet)
 *
 * → Variant C fires tracking in an inline script before page is fully parsed
 * ```
 */

export interface RelativeTimings {
  // PRIMARY METRIC: When experiment tracking fired relative to page navigation start
  // This is your main comparison metric across variants (REQUIRED)
  experiment_fired_at: number

  // When page became interactive (React can start hydrating) (REQUIRED)
  // Compare this to see if variants have different HTML/initial resource load
  page_interactive_at: number

  // Time between page interactive and experiment firing (OPTIONAL - calculated)
  // Positive = fired after interactive (normal), Negative = fired before (edge case)
  // Shows if delay is in React component lifecycle vs page loading
  // May be omitted if calculation fails
  experiment_delay_after_interactive?: number

  // Total time from navigation to page fully loaded (loadEventEnd) (OPTIONAL)
  // Will be 0 if load event hasn't completed yet when tracking fires
  // Common in Next.js/React apps with lazy-loaded assets
  // May be omitted if not available
  page_fully_loaded_at?: number

  // Complete network time: DNS + TCP + SSL + request + response (OPTIONAL)
  // Measured from fetchStart to responseEnd
  // Shows if backend processing or connection setup differs between variants
  // May be omitted if not available
  network_plus_response_time?: number

  // Time to First Byte (TTFB) - server response time (OPTIONAL)
  // Measured from requestStart to responseStart
  // Key metric for identifying server-side performance differences
  // May be omitted if not available
  time_to_first_byte?: number

  // Time to first visual content (OPTIONAL)
  // Indicates if variants render at different speeds
  // May be 0 if browser doesn't support paint timing or omitted if invalid
  first_contentful_paint_at?: number
}

/**
 * Gets performance timing data using the modern Navigation Timing API
 * Returns null on any error to prevent breaking user experience
 */
const getNavigationTiming = () => {
  try {
    if (typeof window === 'undefined' || !window.performance) {
      return null
    }

    // Try modern Navigation Timing API first
    try {
      const navigationEntries = performance.getEntriesByType(
        'navigation',
      ) as PerformanceNavigationTiming[]
      if (navigationEntries.length > 0) {
        const entry = navigationEntries[0]

        // Validate that we have the minimum required properties
        if (
          typeof entry.domInteractive !== 'number' ||
          typeof entry.startTime !== 'number'
        ) {
          return null
        }

        return {
          // Core metrics
          domContentLoadedEventStart: entry.domContentLoadedEventStart ?? 0,
          domContentLoadedEventEnd: entry.domContentLoadedEventEnd ?? 0,
          loadEventEnd: entry.loadEventEnd ?? 0,
          loadEventStart: entry.loadEventStart ?? 0,
          requestStart: entry.requestStart ?? 0,
          responseEnd: entry.responseEnd ?? 0,
          responseStart: entry.responseStart ?? 0,
          navigationStart: entry.startTime ?? 0, // In Navigation Timing API v2, this is startTime
          domComplete: entry.domComplete ?? 0,
          domInteractive: entry.domInteractive ?? 0,

          // Network metrics
          domainLookupStart: entry.domainLookupStart ?? 0,
          domainLookupEnd: entry.domainLookupEnd ?? 0,
          connectStart: entry.connectStart ?? 0,
          connectEnd: entry.connectEnd ?? 0,
          secureConnectionStart: entry.secureConnectionStart ?? 0,

          // Redirect metrics
          redirectStart: entry.redirectStart ?? 0,
          redirectEnd: entry.redirectEnd ?? 0,

          // Fetch metrics
          fetchStart: entry.fetchStart ?? 0,
        }
      }
    } catch {
      // Modern API not supported or threw error, fall through to legacy
    }

    // Fallback to legacy timing API
    const { timing } = performance
    if (timing) {
      // Validate minimum required properties
      if (
        typeof timing.domInteractive !== 'number' ||
        typeof timing.navigationStart !== 'number'
      ) {
        return null
      }

      return {
        // Core metrics
        domContentLoadedEventStart: timing.domContentLoadedEventStart ?? 0,
        domContentLoadedEventEnd: timing.domContentLoadedEventEnd ?? 0,
        loadEventEnd: timing.loadEventEnd ?? 0,
        loadEventStart: timing.loadEventStart ?? 0,
        requestStart: timing.requestStart ?? 0,
        responseEnd: timing.responseEnd ?? 0,
        responseStart: timing.responseStart ?? 0,
        navigationStart: timing.navigationStart ?? 0,
        domComplete: timing.domComplete ?? 0,
        domInteractive: timing.domInteractive ?? 0,

        // Network metrics
        domainLookupStart: timing.domainLookupStart ?? 0,
        domainLookupEnd: timing.domainLookupEnd ?? 0,
        connectStart: timing.connectStart ?? 0,
        connectEnd: timing.connectEnd ?? 0,
        secureConnectionStart: timing.secureConnectionStart ?? 0,

        // Redirect metrics
        redirectStart: timing.redirectStart ?? 0,
        redirectEnd: timing.redirectEnd ?? 0,

        // Fetch metrics
        fetchStart: timing.fetchStart ?? 0,
      }
    }

    return null
  } catch {
    // Any unexpected error - fail gracefully
    return null
  }
}

/**
 * Gets paint timing data
 * Returns empty object on any error to prevent breaking user experience
 */
const getPaintTiming = (): {
  firstPaint?: number
  firstContentfulPaint?: number
} => {
  try {
    if (typeof window === 'undefined' || !window.performance) {
      return {}
    }

    const paintEntries = performance.getEntriesByType('paint')
    const result: { firstPaint?: number; firstContentfulPaint?: number } = {}

    paintEntries.forEach((entry) => {
      if (entry.name === 'first-paint' && typeof entry.startTime === 'number') {
        result.firstPaint = entry.startTime
      } else if (
        entry.name === 'first-contentful-paint' &&
        typeof entry.startTime === 'number'
      ) {
        result.firstContentfulPaint = entry.startTime
      }
    })

    return result
  } catch {
    // Paint timing not supported or error occurred
    return {}
  }
}

/**
 * Calculates timing values relative to a given reference time (now)
 *
 * Returns null only if required metrics fail (experiment_fired_at, page_interactive_at)
 * Optional metrics are individually validated and omitted if invalid
 * This ensures partial data is returned rather than failing completely
 *
 * @param referenceTime - The reference timestamp (defaults to Date.now())
 * @returns RelativeTimings object with required metrics + any valid optional metrics, or null if required metrics fail
 */
export const getRelativePerformanceTiming = (
  referenceTime: number = Date.now(),
) => {
  try {
    const timing = getNavigationTiming()
    if (!timing) {
      return null
    }

    // Validate referenceTime is a valid number
    if (!Number.isFinite(referenceTime) || referenceTime <= 0) {
      return null
    }

    const paint = getPaintTiming()

    // For Navigation Timing API v2, times are relative to timeOrigin
    // For legacy API, times are absolute timestamps
    let isModernAPI = false
    let timeBase = 0

    try {
      isModernAPI = performance.getEntriesByType('navigation').length > 0
      timeBase = isModernAPI ? performance.timeOrigin : 0
    } catch {
      // If we can't determine API version, assume legacy
      isModernAPI = false
      timeBase = 0
    }

    // Calculate time from navigation start to a given event
    const timeFromNavStart = (eventTime: number): number => {
      if (!Number.isFinite(eventTime) || eventTime === 0) return 0
      const result = eventTime - timing.navigationStart
      return Number.isFinite(result) ? result : 0
    }

    // PRIMARY METRIC: When did the experiment tracking fire relative to page start
    const experimentFiredAt = isModernAPI
      ? referenceTime - timeBase - timing.navigationStart
      : referenceTime - timing.navigationStart

    // Validate REQUIRED primary metric
    if (!Number.isFinite(experimentFiredAt) || experimentFiredAt < 0) {
      return null
    }

    // When page became interactive (React can hydrate) - REQUIRED
    const pageInteractiveAt = timeFromNavStart(timing.domInteractive)

    // Validate REQUIRED metric
    if (!Number.isFinite(pageInteractiveAt)) {
      return null
    }

    // Build result with required metrics
    const result: RelativeTimings = {
      experiment_fired_at: experimentFiredAt,
      page_interactive_at: pageInteractiveAt,
    }

    // Add optional metrics only if valid
    // Delay between page interactive and experiment firing
    // Note: Can be negative if tracking fires before page is interactive (valid edge case)
    const experimentDelayAfterInteractive =
      experimentFiredAt - pageInteractiveAt
    if (Number.isFinite(experimentDelayAfterInteractive)) {
      result.experiment_delay_after_interactive =
        experimentDelayAfterInteractive
    }

    // Total time until page fully loaded (loadEventEnd)
    // Note: Will be 0 if load event hasn't completed yet - common in Next.js/React
    const pageFullyLoadedAt = timeFromNavStart(timing.loadEventEnd)
    if (Number.isFinite(pageFullyLoadedAt)) {
      result.page_fully_loaded_at = pageFullyLoadedAt
    }

    // Complete network time: DNS + TCP + SSL + request + response
    // Measured from fetchStart (first network activity) to responseEnd
    const networkPlusResponseTime = timing.responseEnd - timing.fetchStart
    if (
      Number.isFinite(networkPlusResponseTime) &&
      networkPlusResponseTime >= 0
    ) {
      result.network_plus_response_time = networkPlusResponseTime
    }

    // Time to First Byte (TTFB) - server response time
    // Critical metric for identifying server-side performance differences
    const timeToFirstByte = timing.responseStart - timing.requestStart
    if (Number.isFinite(timeToFirstByte) && timeToFirstByte >= 0) {
      result.time_to_first_byte = timeToFirstByte
    }

    // First contentful paint timing (relative to navigation start)
    if (
      paint.firstContentfulPaint &&
      Number.isFinite(paint.firstContentfulPaint)
    ) {
      // Paint timing API returns values relative to time origin, but we need
      // them relative to navigation start for consistency with other metrics
      const fcpRelativeToNavStart = isModernAPI
        ? paint.firstContentfulPaint
        : paint.firstContentfulPaint - timing.navigationStart

      if (
        Number.isFinite(fcpRelativeToNavStart) &&
        fcpRelativeToNavStart >= 0
      ) {
        result.first_contentful_paint_at = fcpRelativeToNavStart
      }
    }

    // Return result with all valid metrics (required + any optional ones that passed validation)
    return result
  } catch {
    // Any unexpected error - fail gracefully
    return null
  }
}

/**
 * Gets raw performance timing data without relative calculations
 * Useful for debugging or when you need absolute timestamps
 * Returns null on any error to prevent breaking user experience
 */
export const getRawPerformanceTiming = () => {
  try {
    return getNavigationTiming()
  } catch {
    return null
  }
}

/**
 * Checks if performance timing data is available
 * Returns false on any error to prevent breaking user experience
 */
export const isPerformanceTimingAvailable = () => {
  try {
    return (
      typeof window !== 'undefined' &&
      window.performance &&
      (performance.getEntriesByType('navigation').length > 0 ||
        !!performance.timing)
    )
  } catch {
    return false
  }
}
